import React, { useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Text, Box } from '@atlaskit/primitives/compiled';

import {
	confluenceLocalStorageInstance as localStorage,
	keys as LOCALSTORAGE_KEYS,
} from '@confluence/storage-manager';
import type { WithFlagsProps } from '@confluence/flags';
import { withFlags } from '@confluence/flags';
import type { DialogsStateContainer } from '@confluence/dialogs';
import { RevertEditorDialog } from '@confluence/editor-conversion/entry-points/RevertEditorDialog';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState';
import { useSpaceKey } from '@confluence/space-utils';
import { useContentType } from '@confluence/page-context';

import { onClickPageRevert } from './utils/onClickPageRevert';

const i18n = defineMessages({
	migrationSuccessFlagTitle: {
		id: 'editor-legacy-content-flags.legacy-content-conversion-success-title',
		defaultMessage: 'Page has been converted',
		description: 'Title for the legacy content conversion success flag',
	},
	migrationSuccessFlagDescription: {
		id: 'editor-legacy-content-flags.legacy-content-conversion-success-description',
		defaultMessage:
			'This page has been converted to the cloud editor, with access to better real-time collaboration, smart links, and AI-powered features',
		description: 'Description for the legacy content conversion success flag',
	},
	migrationSuccessFlagFeedbackLink: {
		id: 'editor-legacy-content-flags.legacy-content-conversion-success-feedback-link',
		defaultMessage: 'Want to share feedback?',
		description: 'Link text for feedback in the legacy content conversion success flag',
	},
	migrationSuccessFlagRevertAction: {
		id: 'editor-legacy-content-flags.legacy-content-conversion-success-revert-action',
		defaultMessage: 'Revert',
		description: 'Action text for reverting the legacy content conversion',
	},
	migrationSuccessFlagWhatsNewAction: {
		id: 'editor-legacy-content-flags.legacy-content-conversion-success-whats-new-action',
		defaultMessage: "What's new?",
		description: 'Action text for learning what is new in the legacy content conversion',
	},
});

export type LegacyContentFlagProps = {
	contentId: string;
	dialogs: DialogsStateContainer;
};

export const LegacyContentFlagsComponent: React.ComponentType<
	WithFlagsProps & LegacyContentFlagProps
> = ({ flags, dialogs, contentId }) => {
	const { formatMessage } = useIntl();
	const { push } = useRouteActions();
	const spaceKey = useSpaceKey();
	const [contentType] = useContentType();

	const hasLocalStorageValue = localStorage.getItem(LOCALSTORAGE_KEYS.PAGE_JUST_CONVERTED);

	const onRevertToLegacyEditorClick = useCallback(() => {
		dialogs.showDialog(RevertEditorDialog, {
			contentId,
			onPageRevert: () => onClickPageRevert({ push, contentType, spaceKey, contentId }),
		});
	}, [contentId, contentType, push, spaceKey, dialogs]);

	useEffect(() => {
		if (hasLocalStorageValue) {
			void flags
				.showSuccessFlag({
					title: formatMessage(i18n.migrationSuccessFlagTitle),
					description: (
						<Box>
							<Text>
								{formatMessage(i18n.migrationSuccessFlagDescription)} <br />
							</Text>
						</Box>
					),
					actions: [
						{
							content: formatMessage(i18n.migrationSuccessFlagRevertAction),
							onClick: () => onRevertToLegacyEditorClick(),
						},
						{
							content: formatMessage(i18n.migrationSuccessFlagWhatsNewAction),
							href: 'https://support.atlassian.com/confluence-cloud/docs/convert-pages-to-the-new-editor',
						},
					],
				})
				.then(() => {
					localStorage.removeItem(LOCALSTORAGE_KEYS.PAGE_JUST_CONVERTED);
				});
		}
	}, [contentId, flags, formatMessage, hasLocalStorageValue, onRevertToLegacyEditorClick]);

	return null;
};

export const LegacyContentFlags = withFlags(LegacyContentFlagsComponent);
